<script lang="ts">
  import { PeriodSlicer } from '@epitome-analytics/dashboard-kit';
  import { store } from './lib/stores.svelte.js';
  import { fetchPeriods } from './lib/queries.js';
  import { getJWT } from './lib/jwt.js';
  import type { Period } from './lib/types.js';

  import Tabs       from './components/Tabs.svelte';
  import Filters    from './components/Filters.svelte';
  import KpiRow     from './components/KpiRow.svelte';
  import Map        from './components/Map.svelte';
  import Rankings   from './components/Rankings.svelte';
  import Diagnostic from './components/Diagnostic.svelte';
  import TaggedPlan from './components/TaggedPlan.svelte';
  import Glossary   from './components/Glossary.svelte';

  /* ── Auth gate ─────────────────────────────────────────────────────── */
  const hasJwt = getJWT() !== null;

  /* ── Bootstrap: load periods, set default ──────────────────────────── */
  $effect(() => {
    if (!hasJwt) return;
    fetchPeriods()
      .then((keys) => {
        const periods: Period[] = keys.map((k, i) => {
          const d = new Date(k);
          return {
            key:   k,
            label: d.toLocaleString('en', { month: 'short', year: 'numeric' }),
            isLatest: i === 0,
          };
        });
        store.periods = periods;
        if (periods.length) store.setPeriod(periods[0].key);
      })
      .catch((e) => (store.bootError = (e as Error).message));
  });
</script>

<div class="epitome-root shell">
  <header class="topbar">
    <div class="brand">
      <span class="eyebrow">PACK001</span>
      <h1>Market Maker</h1>
    </div>
    <div class="topbar-controls">
      {#if store.periods.length}
        <PeriodSlicer
          periods={store.periods}
          value={store.period}
          onChange={(k) => store.setPeriod(k)}
        />
      {/if}
      <button class="link" onclick={() => (store.glossaryOpen = true)}>Glossary</button>
    </div>
  </header>

  {#if !hasJwt}
    <div class="banner error">
      Missing JWT. This dashboard must be opened with a signed iframe URL
      (<code>#t=&lt;jwt&gt;</code>). For local dev, see <code>README.md</code> for a token-minting snippet.
    </div>
  {:else if store.bootError}
    <div class="banner error">Bootstrap failed: {store.bootError}</div>
  {:else}
    <Filters />
    <KpiRow />
    <Tabs />

    <section class="pane">
      {#if store.tab === 'map'}
        <Map />
      {:else if store.tab === 'rankings'}
        <Rankings />
      {:else if store.tab === 'diagnostic'}
        <Diagnostic />
      {/if}
    </section>
  {/if}

  <footer>
    <span>v1.1 · Cube semantic layer · {store.period || '—'}</span>
  </footer>
</div>

<TaggedPlan />
<Glossary />

<style>
  .shell {
    max-width: 1440px;
    margin: 0 auto;
    padding: var(--space-xl) var(--space-xl) var(--space-3xl);
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
  }
  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-xl);
    padding-bottom: var(--space-md);
    border-bottom: 1px solid var(--color-border);
  }
  .brand h1 {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--text-3xl);
    text-transform: uppercase;
    color: var(--color-navy);
    letter-spacing: var(--tracking-tight);
  }
  .topbar-controls { display: flex; align-items: center; gap: var(--space-md); }
  .link {
    background: transparent;
    border: none;
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
    color: var(--color-blue);
    cursor: pointer;
  }
  .link:hover { text-decoration: underline; }
  .pane { padding-top: var(--space-md); }
  .banner {
    padding: var(--space-lg) var(--space-xl);
    border-radius: var(--radius-md);
    font-family: var(--font-ui);
    font-size: var(--text-sm);
  }
  .banner.error { background: var(--color-negative-bg); color: var(--color-negative); }
  code {
    background: rgba(0,0,0,0.05);
    padding: 1px 6px;
    border-radius: var(--radius-sm);
    font-family: var(--font-mono);
  }
  footer {
    margin-top: var(--space-xl);
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    text-align: center;
    font-family: var(--font-ui);
    letter-spacing: var(--tracking-wide);
  }
</style>
